import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Button,
  FileButton,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { Link } from 'react-router';
import { ACIL_DURUM_BELGE_TIP_ETIKETI, SAHA_TURU_DURUM_ETIKETI } from '@isg/validation';
import type { AcilDurumBelgeTipi, AcilDurumOzeti } from '@isg/validation';
import {
  useAcilIletisimEkle,
  useAcilIletisimSil,
  useKontrolluBelgeIndir,
  useKontrolluBelgeSil,
  useKontrolluBelgeYukle,
  useSahaTurlari,
} from '../api/queries';
import { VadeRozeti } from './AcilDurumScreen';
import { QueryStatus } from './parts';
import {
  Bolum,
  KapiSebebi,
  Rozet,
  SatirEylemleri,
  SatirSilmeSebebi,
  SolukSatir,
} from '../sozlesme';
import { ANLAM } from '../theme';
import { gunGirdisiIso, tarihGoster, tamSayiGoster } from '../lib/format';
import { useVarsayilanGun } from '../lib/saha-gunu';

/**
 * ACİL DURUM SAYFASININ BÖLÜMLERİ (Kapsam §29; sözleşme P1-a · P2 · P11).
 *
 * ⚠️ **DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN** (`yetkiler.yazabilir` — §2.40). Hata
 * sessizce yutulmaz: her yazma kancasının hatası `role="alert"` bandında
 * (D1). Onay kutusu/seçim değeri `setState` güncelleyicisinin İÇİNDE
 * `e.currentTarget`tan OKUNMAZ (D1 — tur 8 / F5 ve kurul dersi).
 */

// ── BELGELER (E1) ─────────────────────────────────────────────────────────

export function BelgelerBolumu({ ozet }: { ozet: AcilDurumOzeti }): React.JSX.Element {
  const yukle = useKontrolluBelgeYukle();
  const sil = useKontrolluBelgeSil();
  const indir = useKontrolluBelgeIndir();
  // ✏️ Doküman turu (0b-v): Select YALNIZ acil durumun iki tipini açar — raf tipleri
  // (`KONTROLLU_BELGE_TIP_ETIKETI`nin kalanı) bu sayfadan yüklenmez (§31/K2).
  const [tip, setTip] = useState<AcilDurumBelgeTipi>('acil_durum_plani');
  // Madde 302: varsayılan "bugün" SAHA GÜNÜ (sunucudan) — kullanıcı değiştirir.
  const [yururluk, setYururluk] = useVarsayilanGun();
  const [aciklama, setAciklama] = useState('');
  const [dosya, setDosya] = useState<File | null>(null);
  const hata = yukle.error ?? sil.error;
  const dokunuldu = dosya !== null || aciklama !== '';
  // P12 (madde 224) — kaydedilmemiş belge satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'belge satırı');
  const kaydedilebilir = dosya !== null && gunGirdisiIso(yururluk) !== null;
  return (
    <Bolum baslik="Kontrollü belgeler" testId="acil-durum-belgeler">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="acil-durum-belge-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      {/* Tip başına GÜNCEL durum — vade sunucudan (yürürlük + #17 periyodu). */}
      <Group gap="lg" mb="sm" wrap="wrap">
        {ozet.belgeTipleri.map((b) => (
          <Stack gap={2} key={b.tip} data-testid={`acil-durum-belge-durum-${b.tip}`}>
            <Text size="xs" c="dimmed">
              {b.tipEtiketi}
            </Text>
            {b.guncelBelge ? (
              <VadeRozeti
                vade={b.guncelBelge.yenilemeVadesi}
                durum={b.guncelBelge.vadeDurumu}
                testId={`acil-durum-belge-vade-${b.tip}`}
              />
            ) : (
              <Rozet anlam="dikkat" testId={`acil-durum-belge-yok-${b.tip}`}>
                Yüklenmemiş
              </Rozet>
            )}
          </Stack>
        ))}
      </Group>
      <KapiSebebi
        durum={ozet.yetkiler.yazmaKapisi}
        nesne="Kontrollü belgeyi"
        fiil="yükler"
        testId="acil-durum-belge-yazamama-sebebi"
      />
      <Table data-testid="acil-durum-belge-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={200}>Tip</Table.Th>
            <Table.Th>Dosya</Table.Th>
            <Table.Th w={120}>Yürürlük</Table.Th>
            <Table.Th w={200}>Yenileme vadesi</Table.Th>
            <Table.Th w={150}>Yükümlülük</Table.Th>
            <Table.Th w={60} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {ozet.belgeler.map((b) => (
            <Table.Tr
              key={b.id}
              data-testid={`acil-durum-belge-${b.id}`}
              opacity={b.guncel ? 1 : 0.6}
            >
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  <Text size="sm">{b.tipEtiketi}</Text>
                  {!b.guncel && <Rozet anlam="bilgi">Eski sürüm</Rozet>}
                </Group>
              </Table.Td>
              <Table.Td>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  data-testid={`acil-durum-belge-indir-${b.id}`}
                  onClick={() =>
                    void indir(b.id).then(({ blob, dosyaAdi }) => {
                      const url = URL.createObjectURL(blob);
                      const bag = document.createElement('a');
                      bag.href = url;
                      bag.download = dosyaAdi ?? b.dosyaAdi;
                      document.body.appendChild(bag);
                      bag.click();
                      bag.remove();
                      URL.revokeObjectURL(url);
                    })
                  }
                >
                  {b.dosyaAdi}
                </Button>
                {b.aciklama && (
                  <Text size="xs" c="dimmed">
                    {b.aciklama}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>{tarihGoster(b.yururlukTarihi)}</Table.Td>
              <Table.Td>
                {b.guncel ? (
                  <VadeRozeti vade={b.yenilemeVadesi} durum={b.vadeDurumu} />
                ) : (
                  <Text size="xs" c="dimmed">
                    —
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="xs">{b.yukumlulukKalemAd ?? '—'}</Text>
              </Table.Td>
              <Table.Td>
                {/* P13-c (madde 227) — silme kapısı SATIR BAŞINA sunucudan: yazma →
                    sahiplik (yükleyen VEYA holding yön.). */}
                {b.silmeKapisi.yazabilir && (
                  <SatirEylemleri
                    testId={`acil-durum-belge-eylemler-${b.id}`}
                    digerleri={[
                      {
                        etiket: 'Belgeyi sil',
                        tehlikeli: true,
                        testId: `acil-durum-belge-sil-${b.id}`,
                        onClick: () => sil.mutate({ id: b.id }),
                      },
                    ]}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {ozet.yetkiler.yazabilir && (
            <SolukSatir
              testId="acil-durum-belge-soluk"
              dokunuldu={dokunuldu}
              kaydedilebilir={kaydedilebilir}
              kaydediliyor={yukle.isPending}
              kolonSayisi={5}
              onKaydet={() => {
                if (!dosya) return;
                yukle.mutate(
                  {
                    workplaceId: ozet.workplaceId,
                    dosya,
                    tip,
                    yururlukTarihi: yururluk,
                    ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
                  },
                  {
                    onSuccess: () => {
                      setDosya(null);
                      setAciklama('');
                    },
                  },
                );
              }}
            >
              <Table.Td>
                <Select
                  size="xs"
                  aria-label="Belge tipi"
                  comboboxProps={{ withinPortal: true }}
                  allowDeselect={false}
                  value={tip}
                  onChange={(v) => setTip((v as AcilDurumBelgeTipi) ?? 'acil_durum_plani')}
                  data={Object.entries(ACIL_DURUM_BELGE_TIP_ETIKETI).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                  data-testid="acil-durum-belge-tip"
                />
              </Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <FileButton onChange={setDosya} accept="application/pdf,image/jpeg,image/png">
                    {(props) => (
                      <Button
                        {...props}
                        size="compact-xs"
                        variant="light"
                        data-testid="acil-durum-belge-dosya"
                      >
                        {dosya ? dosya.name : '+ Dosya seç'}
                      </Button>
                    )}
                  </FileButton>
                  <TextInput
                    size="xs"
                    placeholder="Açıklama (isteğe bağlı)"
                    aria-label="Açıklama"
                    value={aciklama}
                    onChange={(e) => setAciklama(e.currentTarget.value)}
                    data-testid="acil-durum-belge-aciklama"
                  />
                </Group>
              </Table.Td>
              <Table.Td colSpan={3}>
                <TextInput
                  size="xs"
                  type="date"
                  aria-label="Yürürlük tarihi"
                  w={160}
                  value={yururluk}
                  onChange={(e) => setYururluk(e.currentTarget.value)}
                  data-testid="acil-durum-belge-yururluk"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {/* Sebep bölüm başına BİR KEZ — ilk kapalı satırın sebebi. */}
      <SatirSilmeSebebi
        kapilar={ozet.belgeler.map((b) => b.silmeKapisi)}
        nesne="Belgeyi"
        fiil="siler"
        testId="acil-durum-belge-silme-sebebi"
      />
    </Bolum>
  );
}

// ── EKİPLER (E2) — görevlendirmeden, ikinci üye kaydı YOK ─────────────────

export function EkiplerBolumu({ ozet }: { ozet: AcilDurumOzeti }): React.JSX.Element {
  return (
    <Bolum baslik="Acil durum ekipleri" testId="acil-durum-ekipler">
      <Table data-testid="acil-durum-ekip-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={220}>Ekip</Table.Th>
            <Table.Th w={200}>Zorunlu sayı</Table.Th>
            <Table.Th>Üyeler (görevlendirmeden)</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {ozet.ekipler.map((e) => (
            <Table.Tr key={e.gorevlendirmeTuruId} data-testid={`acil-durum-ekip-${e.kutuphaneRef}`}>
              <Table.Td>
                <Text size="sm">{e.gorevlendirmeTuruAd}</Text>
              </Table.Td>
              <Table.Td data-testid={`acil-durum-ekip-sayi-${e.kutuphaneRef}`}>
                {e.gereken === null ? (
                  // Bölen boş (madde 175) — sıfır ya da "tam" YAZILMAZ.
                  <Text size="xs" c="dimmed">
                    Tanımsız
                  </Text>
                ) : (
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">
                      {tamSayiGoster(e.yeterli ?? 0)}/{tamSayiGoster(e.gereken)}
                    </Text>
                    <Rozet anlam={(e.eksik ?? 0) > 0 ? 'gecikmis' : 'olumlu'}>
                      {(e.eksik ?? 0) > 0 ? `${tamSayiGoster(e.eksik ?? 0)} eksik` : 'Tam'}
                    </Rozet>
                  </Group>
                )}
              </Table.Td>
              <Table.Td>
                {e.uyeler.length === 0 ? (
                  <Text size="xs" c="dimmed">
                    Görevlendirme yok
                  </Text>
                ) : (
                  <Group gap="xs" wrap="wrap">
                    {e.uyeler.map((u) => (
                      <Group
                        gap={4}
                        wrap="nowrap"
                        key={u.designationId}
                        data-testid={`acil-durum-uye-${u.personId}`}
                      >
                        <Link to={`/calisanlar/${u.personId}`}>{u.adSoyad}</Link>
                        {u.eksik && (
                          <Rozet anlam="gecikmis" testId={`acil-durum-uye-eksik-${u.personId}`}>
                            Eksik
                          </Rozet>
                        )}
                      </Group>
                    ))}
                  </Group>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed" mt="xs">
        Üyelik, kişi kartındaki görevlendirme bölümünden verilir.
      </Text>
    </Bolum>
  );
}

// ── TOPLANMA NOKTALARI (E4) ───────────────────────────────────────────────

export function ToplanmaBolumu({ ozet }: { ozet: AcilDurumOzeti }): React.JSX.Element {
  return (
    <Bolum baslik="Toplanma noktaları" testId="acil-durum-toplanma">
      {ozet.toplanmaNoktalari.length === 0 ? (
        <Text size="sm" c="dimmed">
          Lokasyon ağacında işaretli toplanma noktası yok —{' '}
          <Link to={`/organizasyon/isyeri/${ozet.workplaceId}`}>ağaçta işaretle</Link>.
        </Text>
      ) : (
        <Group gap="xs" wrap="wrap">
          {ozet.toplanmaNoktalari.map((n) => (
            <Rozet anlam="bilgi" key={n.id} testId={`acil-durum-toplanma-${n.id}`}>
              {n.ad}
            </Rozet>
          ))}
        </Group>
      )}
    </Bolum>
  );
}

// ── TATBİKATLAR (E5) — saha turu kabı ─────────────────────────────────────

export function TatbikatBolumu({ ozet }: { ozet: AcilDurumOzeti }): React.JSX.Element {
  const liste = useSahaTurlari({ tip: 'tatbikat', workplaceId: ozet.workplaceId, sayfa: 1 });
  const t = ozet.tatbikat;
  return (
    <Bolum baslik="Tatbikatlar" testId="acil-durum-tatbikatlar">
      <Group gap="lg" mb="sm" wrap="wrap">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Son tatbikat
          </Text>
          <Text size="sm" data-testid="acil-durum-son-tatbikat">
            {t.sonTarih ? (
              <Link to={`/saha-turlari/${t.sonSiteTourId}`}>
                {tarihGoster(t.sonTarih)} · {t.sonTakipNo}
              </Link>
            ) : (
              '—'
            )}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Sonraki vade
          </Text>
          <VadeRozeti vade={t.sonrakiVade} durum={t.vadeDurumu} testId="acil-durum-tatbikat-vade" />
        </Stack>
        {t.acikTatbikatSayisi > 0 && (
          <Rozet anlam="dikkat" testId="acil-durum-acik-tatbikat">
            {tamSayiGoster(t.acikTatbikatSayisi)} açık tatbikat
          </Rozet>
        )}
      </Group>
      {/* P13 · B1/4 — tatbikat SAHA TURU kabının tipidir: kapı saha turu açma kapısı. */}
      <KapiSebebi
        durum={ozet.yetkiler.tatbikatKapisi}
        nesne="Tatbikatı"
        fiil="açar"
        testId="acil-durum-tatbikat-yazamama-sebebi"
      />
      <QueryStatus queries={[liste]} />
      {liste.isSuccess && (
        <Table data-testid="acil-durum-tatbikat-tablosu">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={140}>Takip no</Table.Th>
              <Table.Th w={120}>Tarih</Table.Th>
              <Table.Th w={100}>Durum</Table.Th>
              <Table.Th>Senaryo</Table.Th>
              <Table.Th w={160}>Uygunsuzluk (kapalı/toplam)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {liste.data.satirlar.map((s) => (
              <Table.Tr key={s.id} data-testid={`acil-durum-tatbikat-${s.takipNo}`}>
                <Table.Td>
                  <Link to={`/saha-turlari/${s.id}`}>{s.takipNo}</Link>
                </Table.Td>
                <Table.Td>{tarihGoster(s.tarih)}</Table.Td>
                <Table.Td>
                  <Rozet anlam={s.durum === 'acik' ? 'dikkat' : 'bilgi'}>
                    {SAHA_TURU_DURUM_ETIKETI[s.durum]}
                  </Rozet>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1}>
                    {s.tatbikat?.senaryo ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {tamSayiGoster(s.bulguOzeti.toplam - s.bulguOzeti.acik)}/
                  {tamSayiGoster(s.bulguOzeti.toplam)}
                </Table.Td>
              </Table.Tr>
            ))}
            {liste.data.satirlar.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text size="sm" c="dimmed">
                    Bu işyerinde tatbikat yok.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      )}
    </Bolum>
  );
}

// ── ACİL İLETİŞİM (E6) ────────────────────────────────────────────────────

export function IletisimBolumu({ ozet }: { ozet: AcilDurumOzeti }): React.JSX.Element {
  const ekle = useAcilIletisimEkle();
  const sil = useAcilIletisimSil();
  const [gorev, setGorev] = useState('');
  const [ad, setAd] = useState('');
  const [telefon, setTelefon] = useState('');
  const hata = ekle.error ?? sil.error;
  const dokunuldu = gorev !== '' || ad !== '' || telefon !== '';
  // P12 (madde 224) — kaydedilmemiş iletişim satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'iletişim satırı');
  const kaydedilebilir = gorev.trim() !== '' && ad.trim() !== '' && telefon.trim() !== '';
  return (
    <Bolum baslik="Acil iletişim listesi" testId="acil-durum-iletisim">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="acil-durum-iletisim-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      <KapiSebebi
        durum={ozet.yetkiler.yazmaKapisi}
        nesne="İletişim satırını"
        fiil="ekler"
        testId="acil-durum-iletisim-yazamama-sebebi"
      />
      <Table data-testid="acil-durum-iletisim-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40}>#</Table.Th>
            <Table.Th>Görev / kurum</Table.Th>
            <Table.Th>Ad</Table.Th>
            <Table.Th w={160}>Telefon</Table.Th>
            <Table.Th w={60} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {ozet.iletisim.map((s, i) => (
            <Table.Tr key={s.id} data-testid={`acil-durum-iletisim-${s.id}`}>
              <Table.Td>{i + 1}</Table.Td>
              <Table.Td>{s.gorev}</Table.Td>
              <Table.Td>
                {s.personId ? <Link to={`/calisanlar/${s.personId}`}>{s.ad}</Link> : s.ad}
              </Table.Td>
              <Table.Td>{s.telefon}</Table.Td>
              <Table.Td>
                {ozet.yetkiler.yazabilir && (
                  <SatirEylemleri
                    testId={`acil-durum-iletisim-eylemler-${s.id}`}
                    digerleri={[
                      {
                        etiket: 'Satırı sil',
                        tehlikeli: true,
                        testId: `acil-durum-iletisim-sil-${s.id}`,
                        onClick: () => sil.mutate({ id: s.id }),
                      },
                    ]}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {ozet.yetkiler.yazabilir && (
            <SolukSatir
              testId="acil-durum-iletisim-soluk"
              dokunuldu={dokunuldu}
              kaydedilebilir={kaydedilebilir}
              kaydediliyor={ekle.isPending}
              kolonSayisi={4}
              onKaydet={() =>
                ekle.mutate(
                  {
                    workplaceId: ozet.workplaceId,
                    gorev: gorev.trim(),
                    ad: ad.trim(),
                    telefon: telefon.trim(),
                  },
                  {
                    onSuccess: () => {
                      setGorev('');
                      setAd('');
                      setTelefon('');
                    },
                  },
                )
              }
            >
              <Table.Td />
              <Table.Td>
                <TextInput
                  size="xs"
                  placeholder="+ Görev / kurum (İtfaiye, Hastane, Ekip lideri…)"
                  aria-label="Görev"
                  value={gorev}
                  onChange={(e) => setGorev(e.currentTarget.value)}
                  data-testid="acil-durum-iletisim-gorev"
                />
              </Table.Td>
              <Table.Td>
                <TextInput
                  size="xs"
                  placeholder="Ad"
                  aria-label="Ad"
                  value={ad}
                  onChange={(e) => setAd(e.currentTarget.value)}
                  data-testid="acil-durum-iletisim-ad"
                />
              </Table.Td>
              <Table.Td>
                <TextInput
                  size="xs"
                  placeholder="Telefon"
                  aria-label="Telefon"
                  value={telefon}
                  onChange={(e) => setTelefon(e.currentTarget.value)}
                  data-testid="acil-durum-iletisim-telefon"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
    </Bolum>
  );
}
